import React from "react";
import { Box, Typography, Paper, CircularProgress } from "@mui/material";
import CachedIcon from "@mui/icons-material/Cached";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TaskAltIcon from "@mui/icons-material/TaskAlt";

const StatusFilters = ({
  essays = [],
  statusFilter,
  setStatusFilter,
  theme,
}) => {
  const getFilterCount = (filter) => {
    switch (filter) {
      case "queued":
        return essays.filter((essay) => essay.queued).length;
      case "ready":
        return essays.filter((essay) => !essay.reviewed).length;
      case "reviewed":
        return essays.filter((essay) => essay.reviewed).length;
      default:
        return essays.length;
    }
  };

  const filterCards = [
    {
      id: "all",
      label: "Totalt",
      count: getFilterCount("all"),
      color: "inherit",
      icon: null,
    },
    {
      id: "queued",
      label: "I kö",
      count: getFilterCount("queued"),
      color: theme.palette.warning.main,
      icon: (
        <CachedIcon
          sx={
            getFilterCount("queued") > 0
              ? {
                  animation: "spin 1.5s linear infinite",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                  fontSize: { xs: "1.8rem", sm: "2.5rem" },
                }
              : { fontSize: { xs: "1.8rem", sm: "2.5rem" } }
          }
        />
      ),
    },
    {
      id: "ready",
      label: "Redo för granskning",
      count: getFilterCount("ready"),
      color: theme.palette.primary.main,
      icon: (
        <CheckCircleIcon
          sx={{
            color: "primary.main",
            fontSize: { xs: "1.8rem", sm: "2.5rem" },
          }}
        />
      ),
    },
    {
      id: "reviewed",
      label: "Granskade",
      count: getFilterCount("reviewed"),
      color: "#2E7D32",
      icon: (
        <TaskAltIcon
          sx={{
            color: "rgb(40 121 42)",
            fontSize: { xs: "1.8rem", sm: "2.5rem" },
          }}
        />
      ),
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        gap: { xs: 1, sm: 2 },
        mb: { xs: 2, sm: 4 },
        flexWrap: "wrap",
      }}
    >
      {filterCards.map(({ id, label, count, color, icon }) => (
        <Paper
          key={id}
          onClick={() => setStatusFilter(id)}
          sx={{
            p: { xs: 2, sm: 3 },
            cursor: "pointer",
            flex: "1",
            minWidth: { xs: "calc(50% - 4px)", sm: "200px" },
            border:
              statusFilter === id
                ? `1px solid ${theme.palette.primary.main}`
                : `1px solid ${theme.palette.background.paperHover}`,
            "&:hover": { borderColor: theme.palette.primary.main },
            borderRadius: 3,
            boxShadow: "0 3px 28px rgba(12,8,51,.06)",
            ...(id === "queued" &&
              getFilterCount("queued") > 0 && {
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: "-100%",
                  width: "100%",
                  height: "100%",
                  background:
                    theme.palette.mode === "dark"
                      ? "linear-gradient(to right, transparent, rgba(255,255,255,0.1), transparent)"
                      : "linear-gradient(to right, transparent, rgba(0,0,0,0.05), transparent)",
                  animation: "shimmer 2s infinite",
                  "@keyframes shimmer": {
                    "0%": { left: "-100%" },
                    "100%": { left: "100%" },
                  },
                },
              }),
          }}
        >
          <Typography
            variant="h6"
            color="textSecondary"
            sx={{
              fontSize: { xs: "0.9rem", sm: "1.1rem" },
              mb: { xs: 0.5, sm: 1 },
            }}
          >
            {label}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: "bold",
                color,
                fontSize: { xs: "1.8rem", sm: "3rem" },
              }}
            >
              {count}
            </Typography>
            {icon && (
              <Box
                component="span"
                sx={{
                  color: color,
                  mt: icon.type === CachedIcon ? 1 : 0,
                }}
              >
                {icon}
              </Box>
            )}
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

export default StatusFilters;
